/**
 * assets/diamond-search-carat-aec.js
 *
 * Search / educational Carat AEC — Round foundation + Oval/Pear/Marquise/Cushion/Princess/Emerald/Radiant/Heart/Asscher L×W.
 *
 * Round data flow:
 *   snapped carat → typical Round diameter (mm) → hand px/mm → circular px
 *
 * Oval / Pear / fancy data flow:
 *   snapped carat → typical lengthMm × widthMm → same hand px/mm
 *   → shape overlay width/height. Do not reduce fancy shapes to a diameter.
 *
 * Typical dimensions are educational approximations, not claims about an
 * individual diamond. Future Diamond Detail / Understand Your Diamond
 * should replace these tables with the stone's own geometry.lengthMm /
 * geometry.widthMm.
 *
 * Implemented fancy shapes: Oval, Pear, Marquise, Cushion, Princess,
 * Emerald, Radiant, Heart, Asscher. All 10 Search shapes use their
 * own educational typical-size model.
 *
 * Hand calibration is the approved Size 5.5 master:
 *   859 × 1024
 *   90 px ≡ 15.90 mm at y = 428
 *   shared diamond anchor (325, 428)
 * The Lightest hand is the geometric reference. At y = 428 the ring
 * finger spans 283–367 px (center 325). One anchor for all five tones
 * and every implemented shape.
 */
(function (root) {
  "use strict";

  var HAND_NATIVE_WIDTH = 859;
  var HAND_NATIVE_HEIGHT = 1024;
  var HAND_CALIBRATION_PX = 90;
  var HAND_CALIBRATION_MM = 15.9;
  var HAND_CALIBRATION_Y = 428;
  var ANCHOR_X_PX = 325;
  var ANCHOR_Y_PX = 428;
  var NATIVE_PX_PER_MM = HAND_CALIBRATION_PX / HAND_CALIBRATION_MM;

  var CARAT_MIN = 0.25;
  var CARAT_MAX = 30;
  var CARAT_STEP = 0.05;
  var DEFAULT_CARAT = 1;

  var SKIN_TONE_COUNT = 5;
  var DEFAULT_SKIN_TONE = 1;

  /*
   * Approved Round AEC typical-diameter checkpoints (mm).
   * Educational only — not an individual diamond's measurements.
   */
  var ROUND_CHECKPOINTS = [
    [0.25, 4.0],
    [0.5, 5.0],
    [0.75, 5.75],
    [1, 6.5],
    [1.5, 7.3],
    [2, 8.0],
    [3, 9.1],
    [4, 10.25],
    [5, 11.0],
    [7, 12.1],
    [10, 13.7]
  ];

  /*
   * Educational segmented track. Nine checkpoints occupy equal visual
   * intervals. Numeric interpolation is piecewise-linear inside each
   * adjacent segment — not a numerically linear 0.25–30 ruler.
   * Typical-mm tables still end at 10 ct; values above that continue
   * with cube-root similar-shape scaling so 20 ct and 30 ct grow.
   */
  var EDUCATIONAL_CHECKPOINTS = [0.25, 0.5, 1, 2, 3, 5, 10, 20, 30];

  function buildEqualTrackTicks(points) {
    var ticks = [];
    var last;
    var i;
    if (!points || !points.length) return ticks;
    last = points.length - 1;
    for (i = 0; i < points.length; i++) {
      ticks.push({
        ct: points[i],
        pos: last <= 0 ? 0 : i / last
      });
    }
    return ticks;
  }

  var TRACK_TICKS = buildEqualTrackTicks(EDUCATIONAL_CHECKPOINTS);

  function clamp(n, lo, hi) {
    if (n < lo) return lo;
    if (n > hi) return hi;
    return n;
  }

  function snapCarat(ct) {
    var n = Number(ct);
    if (!isFinite(n)) return DEFAULT_CARAT;
    var stepped = Math.round(n / CARAT_STEP) * CARAT_STEP;
    stepped = Math.round(stepped * 100) / 100;
    return clamp(stepped, CARAT_MIN, CARAT_MAX);
  }

  function buildPchip(points) {
    var xs = [];
    var ys = [];
    var h = [];
    var delta = [];
    var m = [];
    var i;
    var n;
    var w1;
    var w2;
    for (i = 0; i < points.length; i++) {
      xs.push(points[i][0]);
      ys.push(points[i][1]);
    }
    n = xs.length;
    for (i = 0; i < n - 1; i++) {
      h[i] = xs[i + 1] - xs[i];
      delta[i] = (ys[i + 1] - ys[i]) / h[i];
    }
    m[0] = delta[0];
    m[n - 1] = delta[n - 2];
    for (i = 1; i < n - 1; i++) {
      if (delta[i - 1] === 0 || delta[i] === 0 || (delta[i - 1] > 0) !== (delta[i] > 0)) {
        m[i] = 0;
      } else {
        w1 = 2 * h[i] + h[i - 1];
        w2 = h[i] + 2 * h[i - 1];
        m[i] = (w1 + w2) / (w1 / delta[i - 1] + w2 / delta[i]);
      }
    }
    return { xs: xs, ys: ys, h: h, m: m };
  }

  var ROUND_PCHIP = buildPchip(ROUND_CHECKPOINTS);

  /*
   * Educational typical Oval face-up L×W (mm). Classic ~1.37 length-to-
   * width. No project-owned Oval table existed; checkpoints are a
   * monotonic similar-shape model anchored at 1.00 ct = 8.10 × 5.90 mm
   * (a well-spread 1 ct oval vs Round 6.50 mm). Length and width are
   * interpolated independently. Not an individual diamond.
   */
  var OVAL_CHECKPOINTS = [
    [0.25, 5.1, 3.7],
    [0.5, 6.4, 4.65],
    [0.75, 7.35, 5.35],
    [1, 8.1, 5.9],
    [1.5, 9.25, 6.75],
    [2, 10.2, 7.45],
    [3, 11.7, 8.5],
    [4, 12.85, 9.35],
    [5, 13.85, 10.1],
    [7, 15.5, 11.3],
    [10, 17.45, 12.7]
  ];

  function lwAxisPoints(points, axisIndex) {
    var rows = [];
    var i;
    for (i = 0; i < points.length; i++) {
      rows.push([points[i][0], points[i][axisIndex]]);
    }
    return rows;
  }

  var OVAL_LENGTH_PCHIP = buildPchip(lwAxisPoints(OVAL_CHECKPOINTS, 1));
  var OVAL_WIDTH_PCHIP = buildPchip(lwAxisPoints(OVAL_CHECKPOINTS, 2));

  /*
   * Educational typical Pear face-up L×W (mm). Classic ~1.56 length-to-
   * width. No project-owned Pear table existed; checkpoints are a
   * monotonic similar-shape model anchored at 1.00 ct = 8.60 × 5.50 mm
   * (a well-spread 1 ct pear vs Round 6.50 mm / Oval 8.10 × 5.90 mm).
   * Length and width are interpolated independently. Not an individual
   * diamond. The source master is point-up; Carat AEC displays it
   * point-down via an asset-internal 180° rotation around the bbox
   * center. That rotation is not a second finger anchor.
   */
  var PEAR_CHECKPOINTS = [
    [0.25, 5.4, 3.45],
    [0.5, 6.8, 4.35],
    [0.75, 7.8, 5.0],
    [1, 8.6, 5.5],
    [1.5, 9.85, 6.3],
    [2, 10.85, 6.95],
    [3, 12.4, 7.95],
    [4, 13.65, 8.75],
    [5, 14.7, 9.4],
    [7, 16.45, 10.5],
    [10, 18.55, 11.85]
  ];

  var PEAR_LENGTH_PCHIP = buildPchip(lwAxisPoints(PEAR_CHECKPOINTS, 1));
  var PEAR_WIDTH_PCHIP = buildPchip(lwAxisPoints(PEAR_CHECKPOINTS, 2));

  /*
   * Educational typical Marquise face-up L×W (mm). Classic ~2.00
   * length-to-width. No project-owned Marquise table existed;
   * checkpoints are a monotonic similar-shape model anchored at
   * 1.00 ct = 10.00 × 5.00 mm. Length and width are interpolated
   * independently. Not supplier/API measurements. Not an individual
   * diamond. The master is already vertical (point up + point down).
   */
  var MARQUISE_CHECKPOINTS = [
    [0.25, 6.3, 3.15],
    [0.5, 7.95, 3.95],
    [0.75, 9.1, 4.55],
    [1, 10.0, 5.0],
    [1.5, 11.45, 5.7],
    [2, 12.6, 6.3],
    [3, 14.4, 7.2],
    [4, 15.85, 7.95],
    [5, 17.1, 8.55],
    [7, 19.15, 9.55],
    [10, 21.55, 10.75]
  ];

  var MARQUISE_LENGTH_PCHIP = buildPchip(lwAxisPoints(MARQUISE_CHECKPOINTS, 1));
  var MARQUISE_WIDTH_PCHIP = buildPchip(lwAxisPoints(MARQUISE_CHECKPOINTS, 2));

  /*
   * Educational typical Cushion face-up L×W (mm). Classic near-square
   * ~1.03–1.04 length-to-width. No project-owned Cushion table existed;
   * checkpoints are a monotonic similar-shape model anchored at
   * 1.00 ct = 6.00 × 5.80 mm (slightly smaller face-up spread than
   * Round 6.50 mm). Length and width are interpolated independently.
   * Not supplier/API measurements. Not an individual diamond.
   * Architecture keeps independent L×W so an elongated Cushion variant
   * can be added later without rewriting the Carat core.
   */
  var CUSHION_CHECKPOINTS = [
    [0.25, 3.8, 3.65],
    [0.5, 4.75, 4.6],
    [0.75, 5.45, 5.25],
    [1, 6.0, 5.8],
    [1.5, 6.85, 6.65],
    [2, 7.55, 7.3],
    [3, 8.65, 8.35],
    [4, 9.5, 9.2],
    [5, 10.25, 9.9],
    [7, 11.5, 11.1],
    [10, 12.95, 12.5]
  ];

  var CUSHION_LENGTH_PCHIP = buildPchip(lwAxisPoints(CUSHION_CHECKPOINTS, 1));
  var CUSHION_WIDTH_PCHIP = buildPchip(lwAxisPoints(CUSHION_CHECKPOINTS, 2));

  /*
   * Educational typical Princess face-up L×W (mm). Classic square
   * ~1.01–1.02 length-to-width. No project-owned Princess table existed;
   * checkpoints are a monotonic similar-shape model anchored at
   * 1.00 ct = 5.55 × 5.50 mm (smaller face-up spread than Round 6.50 mm
   * and Cushion 6.00 × 5.80 mm because of typical Princess depth).
   * Length and width are interpolated independently. Not supplier/API
   * measurements. Not an individual diamond. Four sharp corners; do not
   * treat as Cushion or Round diameter.
   */
  var PRINCESS_CHECKPOINTS = [
    [0.25, 3.5, 3.45],
    [0.5, 4.4, 4.35],
    [0.75, 5.05, 5.0],
    [1, 5.55, 5.5],
    [1.5, 6.35, 6.25],
    [2, 6.95, 6.85],
    [3, 8.0, 7.9],
    [4, 8.8, 8.7],
    [5, 9.45, 9.35],
    [7, 10.55, 10.45],
    [10, 11.9, 11.75]
  ];

  var PRINCESS_LENGTH_PCHIP = buildPchip(lwAxisPoints(PRINCESS_CHECKPOINTS, 1));
  var PRINCESS_WIDTH_PCHIP = buildPchip(lwAxisPoints(PRINCESS_CHECKPOINTS, 2));

  /*
   * Educational typical Emerald face-up L×W (mm). Classic clipped-corner
   * rectangle ~1.40 length-to-width. No project-owned Emerald table
   * existed; checkpoints are a monotonic similar-shape cube-root model
   * anchored at the approved educational references:
   *   1.00 ct = 7.00 × 5.00 mm
   *   5.00 ct = 11.95 × 8.55 mm
   *   10.00 ct = 15.10 × 10.80 mm
   * Emerald spread is independent of Oval / Marquise / Princess at the
   * same carat. Length and width are interpolated independently. Not
   * supplier/API measurements. Not an individual diamond.
   */
  var EMERALD_CHECKPOINTS = [
    [0.25, 4.4, 3.15],
    [0.5, 5.55, 3.95],
    [0.75, 6.35, 4.55],
    [1, 7.0, 5.0],
    [1.5, 8.0, 5.7],
    [2, 8.8, 6.3],
    [3, 10.1, 7.2],
    [4, 11.1, 7.95],
    [5, 11.95, 8.55],
    [7, 13.4, 9.55],
    [10, 15.1, 10.8]
  ];

  var EMERALD_LENGTH_PCHIP = buildPchip(lwAxisPoints(EMERALD_CHECKPOINTS, 1));
  var EMERALD_WIDTH_PCHIP = buildPchip(lwAxisPoints(EMERALD_CHECKPOINTS, 2));

  /*
   * Educational typical Radiant face-up L×W (mm). Classic elongated
   * clipped-corner brilliant/mixed-cut ~1.35 length-to-width. No
   * project-owned Radiant table existed; checkpoints are a monotonic
   * similar-shape model anchored at:
   *   1.00 ct = 6.80 × 5.05 mm
   *   5.00 ct = 11.65 × 8.65 mm
   *   10.00 ct = 14.65 × 10.90 mm
   * Radiant spread is independent of Emerald at the same carat.
   * Length and width are interpolated independently. Not
   * supplier/API measurements. Not an individual diamond. The source
   * master is somewhat more elongated (~1.47 L/W) than this table;
   * physical L×W is authoritative.
   */
  var RADIANT_CHECKPOINTS = [
    [0.25, 4.3, 3.2],
    [0.5, 5.4, 4.0],
    [0.75, 6.2, 4.6],
    [1, 6.8, 5.05],
    [1.5, 7.8, 5.8],
    [2, 8.55, 6.35],
    [3, 9.8, 7.3],
    [4, 10.8, 8.0],
    [5, 11.65, 8.65],
    [7, 13.0, 9.65],
    [10, 14.65, 10.9]
  ];

  var RADIANT_LENGTH_PCHIP = buildPchip(lwAxisPoints(RADIANT_CHECKPOINTS, 1));
  var RADIANT_WIDTH_PCHIP = buildPchip(lwAxisPoints(RADIANT_CHECKPOINTS, 2));

  /*
   * Educational typical Heart face-up L×W (mm). Classic slightly-wide
   * commercial Heart, L/W = length/width ≈ 0.93. Length is cleft →
   * point; width is lobe → lobe. Approved educational checkpoints:
   *   1.00 ct = 6.20 × 6.65 mm
   *   5.00 ct = 10.60 × 11.35 mm
   *   10.00 ct = 13.35 × 14.35 mm
   * Heart spread is independent of Pear / Round / Cushion at the same
   * carat. Length and width are interpolated independently. Not
   * supplier/API/Nivoda measurements. Not an individual diamond. The
   * production master is naturally wider than long (~0.91 native L/W);
   * physical L×W is authoritative. Orientation: cleft up, lobes up,
   * point down. No rotation. No Heart-specific finger anchor.
   */
  var HEART_CHECKPOINTS = [
    [0.25, 3.9, 4.2],
    [0.5, 4.9, 5.25],
    [0.75, 5.65, 6.05],
    [1, 6.2, 6.65],
    [1.5, 7.1, 7.6],
    [2, 7.8, 8.4],
    [3, 8.95, 9.6],
    [4, 9.85, 10.55],
    [5, 10.6, 11.35],
    [7, 11.85, 12.7],
    [10, 13.35, 14.35]
  ];

  var HEART_LENGTH_PCHIP = buildPchip(lwAxisPoints(HEART_CHECKPOINTS, 1));
  var HEART_WIDTH_PCHIP = buildPchip(lwAxisPoints(HEART_CHECKPOINTS, 2));

  /*
   * Educational typical Asscher face-up L×W (mm). Classic near-square
   * octagonal step-cut, L/W = length/width ≈ 1.02. Approved educational
   * checkpoints:
   *   1.00 ct = 5.65 × 5.55 mm
   *   5.00 ct = 9.70 × 9.50 mm
   *   10.00 ct = 12.20 × 12.00 mm
   * Asscher is a deeper step cut, so its face-up spread is smaller than
   * Round 6.50 mm and Cushion 6.00 × 5.80 mm at 1 ct, and near Princess
   * 5.55 × 5.50 mm without becoming a square brilliant. Independent of
   * Emerald's elongated 7.00 × 5.00 mm. Length and width are interpolated
   * independently. Not supplier/API/Nivoda measurements. Not an
   * individual diamond. The production master is near-square
   * (~0.988 native L/W); physical L×W is authoritative. No Asscher-
   * specific finger anchor.
   */
  var ASSCHER_CHECKPOINTS = [
    [0.25, 3.55, 3.5],
    [0.5, 4.5, 4.4],
    [0.75, 5.15, 5.05],
    [1, 5.65, 5.55],
    [1.5, 6.5, 6.35],
    [2, 7.15, 7.0],
    [3, 8.2, 8.05],
    [4, 9.0, 8.8],
    [5, 9.7, 9.5],
    [7, 10.85, 10.65],
    [10, 12.2, 12.0]
  ];

  var ASSCHER_LENGTH_PCHIP = buildPchip(lwAxisPoints(ASSCHER_CHECKPOINTS, 1));
  var ASSCHER_WIDTH_PCHIP = buildPchip(lwAxisPoints(ASSCHER_CHECKPOINTS, 2));

  /*
   * Asset-internal alignment only. Shared anatomical anchor stays
   * (325, 428). Source PNG is point-up; display rotates 180° so the
   * point faces down and the rounded end faces up.
   */
  var PEAR_ASSET_ALIGN = {
    rotateDeg: 180,
    originX: 0.5,
    originY: 0.5,
    translateX: 0,
    translateY: 0
  };

  var SHAPE_ROUND = "round";
  var SHAPE_OVAL = "oval";
  var SHAPE_PEAR = "pear";
  var SHAPE_MARQUISE = "marquise";
  var SHAPE_CUSHION = "cushion";
  var SHAPE_PRINCESS = "princess";
  var SHAPE_EMERALD = "emerald";
  var SHAPE_RADIANT = "radiant";
  var SHAPE_HEART = "heart";
  var SHAPE_ASSCHER = "asscher";
  var IMPLEMENTED_FANCY_SHAPES = [SHAPE_OVAL, SHAPE_PEAR, SHAPE_MARQUISE, SHAPE_CUSHION, SHAPE_PRINCESS, SHAPE_EMERALD, SHAPE_RADIANT, SHAPE_HEART, SHAPE_ASSCHER];

  function hermite(t, y0, m0, y1, m1, hi) {
    var t2 = t * t;
    var t3 = t2 * t;
    var h00 = 2 * t3 - 3 * t2 + 1;
    var h10 = t3 - 2 * t2 + t;
    var h01 = -2 * t3 + 3 * t2;
    var h11 = t3 - t2;
    return y0 * h00 + hi * m0 * h10 + y1 * h01 + hi * m1 * h11;
  }

  function evalPchip(pchip, carat) {
    var ct = snapCarat(carat);
    var xs = pchip.xs;
    var ys = pchip.ys;
    var i;
    var t;
    var hi;
    var lastX;
    var lastY;
    if (ct <= xs[0]) return ys[0];
    lastX = xs[xs.length - 1];
    lastY = ys[ys.length - 1];
    if (ct >= lastX) {
      if (ct === lastX || lastX <= 0) return lastY;
      return lastY * Math.pow(ct / lastX, 1 / 3);
    }
    for (i = 0; i < xs.length - 1; i++) {
      if (ct <= xs[i + 1]) {
        if (ct === xs[i]) return ys[i];
        if (ct === xs[i + 1]) return ys[i + 1];
        hi = pchip.h[i];
        t = (ct - xs[i]) / hi;
        return hermite(t, ys[i], pchip.m[i], ys[i + 1], pchip.m[i + 1], hi);
      }
    }
    return lastY;
  }

  function typicalRoundDiameterMm(carat) {
    return evalPchip(ROUND_PCHIP, carat);
  }

  function typicalOvalLengthMm(carat) {
    return evalPchip(OVAL_LENGTH_PCHIP, carat);
  }

  function typicalOvalWidthMm(carat) {
    return evalPchip(OVAL_WIDTH_PCHIP, carat);
  }

  function typicalOvalSizeMm(carat) {
    return {
      lengthMm: typicalOvalLengthMm(carat),
      widthMm: typicalOvalWidthMm(carat)
    };
  }

  function typicalPearLengthMm(carat) {
    return evalPchip(PEAR_LENGTH_PCHIP, carat);
  }

  function typicalPearWidthMm(carat) {
    return evalPchip(PEAR_WIDTH_PCHIP, carat);
  }

  function typicalPearSizeMm(carat) {
    return {
      lengthMm: typicalPearLengthMm(carat),
      widthMm: typicalPearWidthMm(carat)
    };
  }

  function typicalMarquiseLengthMm(carat) {
    return evalPchip(MARQUISE_LENGTH_PCHIP, carat);
  }

  function typicalMarquiseWidthMm(carat) {
    return evalPchip(MARQUISE_WIDTH_PCHIP, carat);
  }

  function typicalMarquiseSizeMm(carat) {
    return {
      lengthMm: typicalMarquiseLengthMm(carat),
      widthMm: typicalMarquiseWidthMm(carat)
    };
  }

  function typicalCushionLengthMm(carat) {
    return evalPchip(CUSHION_LENGTH_PCHIP, carat);
  }

  function typicalCushionWidthMm(carat) {
    return evalPchip(CUSHION_WIDTH_PCHIP, carat);
  }

  function typicalCushionSizeMm(carat) {
    return {
      lengthMm: typicalCushionLengthMm(carat),
      widthMm: typicalCushionWidthMm(carat)
    };
  }

  function typicalPrincessLengthMm(carat) {
    return evalPchip(PRINCESS_LENGTH_PCHIP, carat);
  }

  function typicalPrincessWidthMm(carat) {
    return evalPchip(PRINCESS_WIDTH_PCHIP, carat);
  }

  function typicalPrincessSizeMm(carat) {
    return {
      lengthMm: typicalPrincessLengthMm(carat),
      widthMm: typicalPrincessWidthMm(carat)
    };
  }

  function typicalEmeraldLengthMm(carat) {
    return evalPchip(EMERALD_LENGTH_PCHIP, carat);
  }

  function typicalEmeraldWidthMm(carat) {
    return evalPchip(EMERALD_WIDTH_PCHIP, carat);
  }

  function typicalEmeraldSizeMm(carat) {
    return {
      lengthMm: typicalEmeraldLengthMm(carat),
      widthMm: typicalEmeraldWidthMm(carat)
    };
  }

  function typicalRadiantLengthMm(carat) {
    return evalPchip(RADIANT_LENGTH_PCHIP, carat);
  }

  function typicalRadiantWidthMm(carat) {
    return evalPchip(RADIANT_WIDTH_PCHIP, carat);
  }

  function typicalRadiantSizeMm(carat) {
    return {
      lengthMm: typicalRadiantLengthMm(carat),
      widthMm: typicalRadiantWidthMm(carat)
    };
  }

  function typicalHeartLengthMm(carat) {
    return evalPchip(HEART_LENGTH_PCHIP, carat);
  }

  function typicalHeartWidthMm(carat) {
    return evalPchip(HEART_WIDTH_PCHIP, carat);
  }

  function typicalHeartSizeMm(carat) {
    return {
      lengthMm: typicalHeartLengthMm(carat),
      widthMm: typicalHeartWidthMm(carat)
    };
  }

  function typicalAsscherLengthMm(carat) {
    return evalPchip(ASSCHER_LENGTH_PCHIP, carat);
  }

  function typicalAsscherWidthMm(carat) {
    return evalPchip(ASSCHER_WIDTH_PCHIP, carat);
  }

  function typicalAsscherSizeMm(carat) {
    return {
      lengthMm: typicalAsscherLengthMm(carat),
      widthMm: typicalAsscherWidthMm(carat)
    };
  }

  function resolveSearchShape(raw) {
    var key = String(raw == null ? "" : raw)
      .trim()
      .toLowerCase()
      .replace(/[_]+/g, "-")
      .replace(/\s+/g, "-");
    if (key === SHAPE_OVAL) return SHAPE_OVAL;
    if (key === SHAPE_PEAR) return SHAPE_PEAR;
    if (key === SHAPE_MARQUISE) return SHAPE_MARQUISE;
    if (key === SHAPE_CUSHION) return SHAPE_CUSHION;
    if (key === SHAPE_PRINCESS) return SHAPE_PRINCESS;
    if (key === SHAPE_EMERALD) return SHAPE_EMERALD;
    if (key === SHAPE_RADIANT) return SHAPE_RADIANT;
    if (key === SHAPE_HEART) return SHAPE_HEART;
    if (key === SHAPE_ASSCHER) return SHAPE_ASSCHER;
    return SHAPE_ROUND;
  }

  function resolveSearchDiameterMm(carat) {
    return typicalRoundDiameterMm(carat);
  }

  function resolveSearchSize(carat, shape) {
    var key = resolveSearchShape(shape);
    var oval;
    var pear;
    var marquise;
    var cushion;
    var princess;
    var emerald;
    var radiant;
    var heart;
    var asscher;
    var diameter;
    if (key === SHAPE_OVAL) {
      oval = typicalOvalSizeMm(carat);
      return {
        shape: SHAPE_OVAL,
        kind: "lw",
        orientation: "vertical",
        lengthMm: oval.lengthMm,
        widthMm: oval.widthMm,
        diameterMm: null
      };
    }
    if (key === SHAPE_PEAR) {
      pear = typicalPearSizeMm(carat);
      return {
        shape: SHAPE_PEAR,
        kind: "lw",
        orientation: "vertical",
        point: "down",
        lengthMm: pear.lengthMm,
        widthMm: pear.widthMm,
        diameterMm: null
      };
    }
    if (key === SHAPE_MARQUISE) {
      marquise = typicalMarquiseSizeMm(carat);
      return {
        shape: SHAPE_MARQUISE,
        kind: "lw",
        orientation: "vertical",
        point: "both",
        lengthMm: marquise.lengthMm,
        widthMm: marquise.widthMm,
        diameterMm: null
      };
    }
    if (key === SHAPE_CUSHION) {
      cushion = typicalCushionSizeMm(carat);
      return {
        shape: SHAPE_CUSHION,
        kind: "lw",
        orientation: "vertical",
        lengthMm: cushion.lengthMm,
        widthMm: cushion.widthMm,
        diameterMm: null
      };
    }
    if (key === SHAPE_PRINCESS) {
      princess = typicalPrincessSizeMm(carat);
      return {
        shape: SHAPE_PRINCESS,
        kind: "lw",
        orientation: "vertical",
        lengthMm: princess.lengthMm,
        widthMm: princess.widthMm,
        diameterMm: null
      };
    }
    if (key === SHAPE_EMERALD) {
      emerald = typicalEmeraldSizeMm(carat);
      return {
        shape: SHAPE_EMERALD,
        kind: "lw",
        orientation: "vertical",
        lengthMm: emerald.lengthMm,
        widthMm: emerald.widthMm,
        diameterMm: null
      };
    }
    if (key === SHAPE_RADIANT) {
      radiant = typicalRadiantSizeMm(carat);
      return {
        shape: SHAPE_RADIANT,
        kind: "lw",
        orientation: "vertical",
        lengthMm: radiant.lengthMm,
        widthMm: radiant.widthMm,
        diameterMm: null
      };
    }
    if (key === SHAPE_HEART) {
      heart = typicalHeartSizeMm(carat);
      return {
        shape: SHAPE_HEART,
        kind: "lw",
        orientation: "vertical",
        point: "down",
        cleft: "up",
        lengthMm: heart.lengthMm,
        widthMm: heart.widthMm,
        diameterMm: null
      };
    }
    if (key === SHAPE_ASSCHER) {
      asscher = typicalAsscherSizeMm(carat);
      return {
        shape: SHAPE_ASSCHER,
        kind: "lw",
        orientation: "vertical",
        lengthMm: asscher.lengthMm,
        widthMm: asscher.widthMm,
        diameterMm: null
      };
    }
    diameter = typicalRoundDiameterMm(carat);
    return {
      shape: SHAPE_ROUND,
      kind: "diameter",
      orientation: "circular",
      lengthMm: diameter,
      widthMm: diameter,
      diameterMm: diameter
    };
  }

  function displayedPxPerMm(handDisplayWidthPx) {
    var w = Number(handDisplayWidthPx);
    if (!isFinite(w) || w <= 0) w = HAND_NATIVE_WIDTH;
    return NATIVE_PX_PER_MM * (w / HAND_NATIVE_WIDTH);
  }

  function renderedDiameterPx(diameterMm, handDisplayWidthPx) {
    var mm = Number(diameterMm);
    if (!isFinite(mm) || mm < 0) return 0;
    return mm * displayedPxPerMm(handDisplayWidthPx);
  }

  function lerp(a, b, t) {
    return a + (b - a) * t;
  }

  function caratToTrackPos(carat) {
    var ct = snapCarat(carat);
    var ticks = TRACK_TICKS;
    var i;
    var a;
    var b;
    var t;
    if (ct <= ticks[0].ct) return ticks[0].pos;
    for (i = 1; i < ticks.length; i++) {
      if (ct <= ticks[i].ct) {
        a = ticks[i - 1];
        b = ticks[i];
        t = (ct - a.ct) / (b.ct - a.ct);
        return lerp(a.pos, b.pos, t);
      }
    }
    return ticks[ticks.length - 1].pos;
  }

  function trackPosToCarat(pos) {
    var p = clamp(Number(pos), 0, 1);
    var ticks = TRACK_TICKS;
    var i;
    var a;
    var b;
    var t;
    if (!isFinite(p)) return DEFAULT_CARAT;
    if (p <= ticks[0].pos) return snapCarat(ticks[0].ct);
    for (i = 1; i < ticks.length; i++) {
      if (p <= ticks[i].pos) {
        a = ticks[i - 1];
        b = ticks[i];
        t = (p - a.pos) / (b.pos - a.pos);
        return snapCarat(lerp(a.ct, b.ct, t));
      }
    }
    return snapCarat(ticks[ticks.length - 1].ct);
  }

  function formatCaratLabel(carat) {
    return snapCarat(carat).toFixed(2) + " CT";
  }

  function formatTrackTickLabel(carat) {
    var n = Number(carat);
    if (!isFinite(n)) return "";
    if (Math.abs(n - Math.round(n)) < 1e-6) return String(Math.round(n));
    if (Math.abs(n * 10 - Math.round(n * 10)) < 1e-6) return String(Math.round(n * 10) / 10);
    return n.toFixed(2);
  }

  function formatTypicalMm(mm) {
    var n = Number(mm);
    var two;
    var one;
    if (!isFinite(n) || n < 0) return "0.0";
    two = Math.round(n * 100) / 100;
    one = Math.round(n * 10) / 10;
    if (Math.abs(two - one) < 0.001) return one.toFixed(1);
    return two.toFixed(2);
  }

  function formatTypicalDiameter(mm) {
    return "Typical Diameter: " + formatTypicalMm(mm) + " mm";
  }

  function formatTypicalSize(lengthMm, widthMm) {
    return "Typical Size: " + formatTypicalMm(lengthMm) + " \u00d7 " + formatTypicalMm(widthMm) + " mm";
  }

  /*
   * Slider value-bubble layout only. Does not change track mapping,
   * typical-mm calibration, or diamond px. halfPx is the visual half-width
   * of the "30.00 CT" bubble so endpoints stay inside the slider.
   */
  var BUBBLE_HALF_PX = 36;

  function clampBubbleLayout(trackPos, containerWidth, bubbleHalfPx) {
    var p = clamp(Number(trackPos), 0, 1);
    var w = Number(containerWidth);
    var half = Number(bubbleHalfPx);
    var thumbX;
    var left;
    if (!isFinite(p)) p = caratToTrackPos(DEFAULT_CARAT);
    if (!isFinite(half) || half < 0) half = BUBBLE_HALF_PX;
    if (!isFinite(w) || w <= 0) {
      return { leftPx: null, leftPct: p * 100, pointerShiftPx: 0 };
    }
    if (w < half * 2) half = w / 2;
    thumbX = p * w;
    left = clamp(thumbX, half, w - half);
    return {
      leftPx: left,
      leftPct: null,
      pointerShiftPx: thumbX - left
    };
  }

  var api = {
    HAND_NATIVE_WIDTH: HAND_NATIVE_WIDTH,
    HAND_NATIVE_HEIGHT: HAND_NATIVE_HEIGHT,
    HAND_CALIBRATION_PX: HAND_CALIBRATION_PX,
    HAND_CALIBRATION_MM: HAND_CALIBRATION_MM,
    HAND_CALIBRATION_Y: HAND_CALIBRATION_Y,
    ANCHOR_X_PX: ANCHOR_X_PX,
    ANCHOR_Y_PX: ANCHOR_Y_PX,
    ANCHOR_X: ANCHOR_X_PX / HAND_NATIVE_WIDTH,
    ANCHOR_Y: ANCHOR_Y_PX / HAND_NATIVE_HEIGHT,
    NATIVE_PX_PER_MM: NATIVE_PX_PER_MM,
    CARAT_MIN: CARAT_MIN,
    CARAT_MAX: CARAT_MAX,
    CARAT_STEP: CARAT_STEP,
    DEFAULT_CARAT: DEFAULT_CARAT,
    SKIN_TONE_COUNT: SKIN_TONE_COUNT,
    DEFAULT_SKIN_TONE: DEFAULT_SKIN_TONE,
    ROUND_CHECKPOINTS: ROUND_CHECKPOINTS,
    OVAL_CHECKPOINTS: OVAL_CHECKPOINTS,
    PEAR_CHECKPOINTS: PEAR_CHECKPOINTS,
    MARQUISE_CHECKPOINTS: MARQUISE_CHECKPOINTS,
    CUSHION_CHECKPOINTS: CUSHION_CHECKPOINTS,
    PRINCESS_CHECKPOINTS: PRINCESS_CHECKPOINTS,
    EMERALD_CHECKPOINTS: EMERALD_CHECKPOINTS,
    RADIANT_CHECKPOINTS: RADIANT_CHECKPOINTS,
    HEART_CHECKPOINTS: HEART_CHECKPOINTS,
    ASSCHER_CHECKPOINTS: ASSCHER_CHECKPOINTS,
    PEAR_ASSET_ALIGN: PEAR_ASSET_ALIGN,
    EDUCATIONAL_CHECKPOINTS: EDUCATIONAL_CHECKPOINTS,
    TRACK_TICKS: TRACK_TICKS,
    SHAPE_ROUND: SHAPE_ROUND,
    SHAPE_OVAL: SHAPE_OVAL,
    SHAPE_PEAR: SHAPE_PEAR,
    SHAPE_MARQUISE: SHAPE_MARQUISE,
    SHAPE_CUSHION: SHAPE_CUSHION,
    SHAPE_PRINCESS: SHAPE_PRINCESS,
    SHAPE_EMERALD: SHAPE_EMERALD,
    SHAPE_RADIANT: SHAPE_RADIANT,
    SHAPE_HEART: SHAPE_HEART,
    SHAPE_ASSCHER: SHAPE_ASSCHER,
    IMPLEMENTED_FANCY_SHAPES: IMPLEMENTED_FANCY_SHAPES,
    snapCarat: snapCarat,
    typicalRoundDiameterMm: typicalRoundDiameterMm,
    typicalOvalLengthMm: typicalOvalLengthMm,
    typicalOvalWidthMm: typicalOvalWidthMm,
    typicalOvalSizeMm: typicalOvalSizeMm,
    typicalPearLengthMm: typicalPearLengthMm,
    typicalPearWidthMm: typicalPearWidthMm,
    typicalPearSizeMm: typicalPearSizeMm,
    typicalMarquiseLengthMm: typicalMarquiseLengthMm,
    typicalMarquiseWidthMm: typicalMarquiseWidthMm,
    typicalMarquiseSizeMm: typicalMarquiseSizeMm,
    typicalCushionLengthMm: typicalCushionLengthMm,
    typicalCushionWidthMm: typicalCushionWidthMm,
    typicalCushionSizeMm: typicalCushionSizeMm,
    typicalPrincessLengthMm: typicalPrincessLengthMm,
    typicalPrincessWidthMm: typicalPrincessWidthMm,
    typicalPrincessSizeMm: typicalPrincessSizeMm,
    typicalEmeraldLengthMm: typicalEmeraldLengthMm,
    typicalEmeraldWidthMm: typicalEmeraldWidthMm,
    typicalEmeraldSizeMm: typicalEmeraldSizeMm,
    typicalRadiantLengthMm: typicalRadiantLengthMm,
    typicalRadiantWidthMm: typicalRadiantWidthMm,
    typicalRadiantSizeMm: typicalRadiantSizeMm,
    typicalHeartLengthMm: typicalHeartLengthMm,
    typicalHeartWidthMm: typicalHeartWidthMm,
    typicalHeartSizeMm: typicalHeartSizeMm,
    typicalAsscherLengthMm: typicalAsscherLengthMm,
    typicalAsscherWidthMm: typicalAsscherWidthMm,
    typicalAsscherSizeMm: typicalAsscherSizeMm,
    resolveSearchShape: resolveSearchShape,
    resolveSearchDiameterMm: resolveSearchDiameterMm,
    resolveSearchSize: resolveSearchSize,
    displayedPxPerMm: displayedPxPerMm,
    renderedDiameterPx: renderedDiameterPx,
    caratToTrackPos: caratToTrackPos,
    trackPosToCarat: trackPosToCarat,
    formatCaratLabel: formatCaratLabel,
    formatTrackTickLabel: formatTrackTickLabel,
    formatTypicalMm: formatTypicalMm,
    formatTypicalDiameter: formatTypicalDiameter,
    formatTypicalSize: formatTypicalSize,
    BUBBLE_HALF_PX: BUBBLE_HALF_PX,
    clampBubbleLayout: clampBubbleLayout
  };

  if (typeof window !== "undefined") window.IvanovCaratAec = api;
  if (typeof module === "object" && module.exports) module.exports = api;
})(typeof globalThis !== "undefined" ? globalThis : this);
